import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { 
  MessageSquare,
  Minimize2,
  Maximize2,
  X,
  Send,
  Mic,
  MicOff,
  Bot,
  User,
  Loader2,
  MessageCircle,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import useTranslation from '@/hooks/use-translation';
import useIsMobile from '@/hooks/use-mobile';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMode } from '@/contexts/ModeContext';
import { chatService, Message } from '@/lib/services/chatService';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

interface ChatAssistantProps {
  className?: string;
  isFullPage?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

const ChatAssistant: React.FC<ChatAssistantProps> = ({
  className,
  isFullPage = false,
  isOpen: propIsOpen,
  onClose
}) => {
  const { mode } = useMode();
  const { translate } = useTranslation();
  const { toast: useToastToast } = useToast();
  const [isOpen, setIsOpen] = useState(propIsOpen ?? false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { isRTL } = useLanguage();
  const isMobile = useIsMobile();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [hasLocation, setHasLocation] = useState(false);

  useEffect(() => {
    if (propIsOpen !== undefined) {
      setIsOpen(propIsOpen);
    }
  }, [propIsOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isMinimized]);
  
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        content: chatService.getWelcomeMessage(mode),
        type: 'assistant',
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
      setSuggestions(chatService.getSuggestions(mode));

      // Request location permission
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          () => setHasLocation(true),
          () => {
            useToastToast({
              title: translate('common.locationNeeded'),
              description: translate('common.locationDescription'),
              action: (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    navigator.geolocation.getCurrentPosition(
                      () => setHasLocation(true),
                      () => setHasLocation(false)
                    );
                  }}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  {translate('common.enableLocation')}
                </Button>
              ),
            });
          }
        );
      }
    }
  }, [isOpen, mode, messages.length, useToastToast, translate]);
  
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (content: string = input) => {
    if (!content.trim()) return;
    
    const userMessage: Message = {
      id: crypto.randomUUID(),
      content: content,
      type: 'user',
      timestamp: new Date().toISOString(),
    };

    const assistantMessage: Message = {
      id: crypto.randomUUID(),
      content: '',
      type: 'assistant',
      timestamp: new Date().toISOString(),
      status: 'sending',
    };

    setMessages(prev => [...prev, userMessage, assistantMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await chatService.getResponse(content, mode);
      setMessages(prev => 
        prev.map(msg => 
          msg.id === assistantMessage.id 
            ? { ...msg, content: response.message, status: 'sent' }
            : msg
        )
      );
      if (response.suggestions) {
        setSuggestions(response.suggestions);
      }
    } catch (error) {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === assistantMessage.id 
            ? { ...msg, content: translate('common.errorMessage'), status: 'error' }
            : msg
        )
      );
    } finally {
      setIsTyping(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleVoiceStart = () => {
    toast.success(translate("chatbot.voiceRecording.started"));
    setIsListening(true);
  };

  const handleVoiceStop = () => {
    toast.success(translate("chatbot.voiceRecording.stopped"));
    setIsListening(false);
  };

  const handleVoiceResult = () => {
    toast.success(translate("chatbot.voiceRecording.completed"));
    setInput(translate("chatbot.responses.general.0"));
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    handleSendMessage(suggestion);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
  };

  if (!isOpen && !isFullPage) {
  return (
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
      >
        <Button
          size="icon"
          className="fixed bottom-4 right-4 h-12 w-12 rounded-full shadow-lg"
          onClick={toggleOpen}
          aria-label={translate("chatbot.ui.openChat")}
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      </motion.div>
    );
  }

  const chatWindow = (
    <Card className={cn(
      "flex flex-col",
      isFullPage ? "h-[calc(100vh-12rem)]" : "h-[500px] w-[380px]",
      className
    )}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4">
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          <span>{translate("AquaGenius AI")}</span>
        </CardTitle>
        <div className="flex items-center gap-1">
            {!isFullPage && (
            <Button
              variant="ghost"
              size="icon"
                  onClick={toggleMinimize}
              className="h-8 w-8"
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </Button>
            )}
            {!isFullPage && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="flex-1 flex flex-col"
          >
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-muted-foreground">
                    <p className="mb-4">{translate("chatbot.welcome")}</p>
                    <div className="space-y-2">
                      <p className="font-medium">{translate("chatbot.suggestedQuestions")}</p>
                      <div className="mb-4 flex flex-wrap gap-2">
                        {suggestions.map((suggestion) => (
                          <Button
                            key={suggestion}
                            variant="secondary"
                            size="sm"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  messages.map(message => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={cn(
                        "flex gap-3",
                        message.type === 'assistant' ? 'justify-start' : 'justify-end',
                        isRTL && 'flex-row-reverse'
                      )}
                    >
                      {message.type === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Bot className="h-4 w-4 text-primary" />
                        </div>
                      )}
        <div 
          className={cn(
                          "rounded-lg px-4 py-2 max-w-[80%] relative",
                          message.type === 'assistant'
                            ? 'bg-muted text-foreground'
                            : 'bg-primary text-primary-foreground'
                        )}
                      >
                        {message.content}
                        {message.status === 'sending' && (
                          <div className="absolute -bottom-6 left-0">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        )}
              </div>
                      {message.type === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-4 w-4 text-primary" />
            </div>
          )}
                    </motion.div>
                  ))
                )}
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-2 text-muted-foreground"
                  >
                    <Bot className="h-4 w-4" />
                    <span className="text-sm">AquaGenius is typing...</span>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            <div className="border-t p-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className={cn(
                  "flex items-center gap-2",
                  isRTL && 'flex-row-reverse'
                )}
              >
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={isListening ? handleVoiceStop : handleVoiceStart}
          className={cn(
                    "flex-shrink-0",
                    isListening && "text-destructive"
                  )}
                >
                  {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>

              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                  placeholder={translate("chatbot.placeholder")}
                className={cn(
                    "flex-1 bg-transparent border-none outline-none placeholder:text-muted-foreground",
                    isRTL && "text-right"
                  )}
                />

              <Button
                  type="submit"
                size="icon"
                  disabled={!input.trim() || isTyping}
                  className="flex-shrink-0"
                >
                  <Send className="h-5 w-5" />
              </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );

  if (isFullPage) {
    return chatWindow;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
                className={cn(
        "fixed bottom-4 right-4 z-50",
        isRTL && "left-4 right-auto"
      )}
    >
      {chatWindow}
    </motion.div>
  );
};

export default ChatAssistant;
